import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import Skeleton from "react-loading-skeleton";
import { ChevronRight, Clock, ShoppingBag } from "lucide-react";
import { getMyOrders } from "./services";
import LoadMore from "../shared/LoadMore";

/* ── Status config ───────────────────────────── */
const STATUS = {
  pending:    { label: "Pending",    bg: "#fef3c7", color: "#92400e", dot: "#f59e0b" },
  processing: { label: "Processing", bg: "#dbeafe", color: "#1e40af", dot: "#0d6efd" },
  confirmed:  { label: "Confirmed",  bg: "#dbeafe", color: "#1e40af", dot: "#0d6efd" },
  completed:  { label: "Completed",  bg: "#dcfce7", color: "#166534", dot: "#059669" },
  delivered:  { label: "Delivered",  bg: "#dcfce7", color: "#166534", dot: "#059669" },
  rejected:   { label: "Rejected",   bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
  cancelled:  { label: "Cancelled",  bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
};

const PAYMENT = {
  paid:        { label: "Paid",   bg: "#dcfce7", color: "#166534" },
  cod_pending: { label: "COD",    bg: "#fef3c7", color: "#92400e" },
  unpaid:      { label: "Unpaid", bg: "#fee2e2", color: "#991b1b" },
  pending:     { label: "Unpaid", bg: "#fee2e2", color: "#991b1b" },
  failed:      { label: "Failed", bg: "#fee2e2", color: "#991b1b" },
  refunded:    { label: "Refunded", bg: "#dbeafe", color: "#1e40af" },
};

const ACTIVE_S    = ["pending", "processing", "confirmed"];

/**
 * The API filters on exactly one status, so tabs that map to a single one are
 * filtered server-side (and paginate correctly). "Active" spans five statuses
 * with no way to express that in one query, so it stays a client-side view over
 * the unfiltered list — which is why it shows a "+" while more pages remain.
 */
const TABS = [
  { key: "all",       label: "All",       status: null },
  { key: "active",    label: "Active",    status: null, clientFiltered: true },
  { key: "delivered", label: "Delivered", status: "DELIVERED" },
  { key: "cancelled", label: "Cancelled", status: "CANCELLED" },
];

/* ── Helpers ─────────────────────────────────── */
const fmt  = (v) => `৳${Number(v || 0).toLocaleString("en-BD")}`;
const fmtD = (iso) => {
  if (!iso) return "N/A";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return "N/A"; }
};

/* ── StatusChip ──────────────────────────────── */
function StatusChip({ status, label, small }) {
  const cfg = STATUS[status?.toLowerCase()] || STATUS.pending;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: cfg.bg, color: cfg.color, borderRadius: 20,
      padding: small ? "2px 8px" : "3px 10px",
      fontSize: small ? 10 : 11, fontWeight: 700,
      fontFamily: "'Inter', sans-serif", textTransform: "capitalize", flexShrink: 0,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: cfg.dot }} />
      {label || cfg.label}
    </span>
  );
}

/* ── OrderCard ───────────────────────────────── */
function OrderCard({ order }) {
  const s      = order?.status?.toLowerCase();
  const cfg    = STATUS[s] || STATUS.pending;
  const isActive = ACTIVE_S.includes(s);
  const items  = order?.items || [];
  // Order items are price/name snapshots — the API keeps no image on them.
  const thumbs = items.slice(0, 3).map(i => i?.image).filter(Boolean);
  const extra  = items.length - 3;
  const paymentCfg = PAYMENT[order?.paymentStatus] || null;

  const itemSummary = items
    .slice(0, 2)
    .map(i => `${i?.name} ×${i?.quantity}`)
    .join("  ·  ");
  const moreItems = items.length > 2 ? `  +${items.length - 2} more` : "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.16 }}
      style={{
        background: "#fff",
        border: "1px solid #f1f5f9",
        borderLeft: `4px solid ${cfg.dot}`,
        borderRadius: 12,
        marginBottom: 10,
        fontFamily: "'Inter', sans-serif",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>

        {/* Row 1 — ID · chips · date */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: "#111827" }}>
              #{order?.orderNumber || order?._id?.slice(-8)?.toUpperCase()}
            </span>
            <StatusChip status={order?.status} label={order?.statusLabel} small />
            {paymentCfg && (
              <span style={{
                background: paymentCfg.bg, color: paymentCfg.color,
                borderRadius: 20, padding: "2px 8px",
                fontSize: 10, fontWeight: 600,
              }}>
                {paymentCfg.label}
              </span>
            )}
          </div>
          <span style={{ fontSize: 11, color: "#9ca3af", flexShrink: 0 }}>
            {fmtD(order?.createdAt)}
          </span>
        </div>

        {/* Row 2 — thumbnails + item summary */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {thumbs.length > 0 && (
            <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
              {thumbs.map((img, i) => (
                <img key={i} src={img} alt=""
                  style={{ width: 38, height: 38, borderRadius: 6, objectFit: "cover", border: "1px solid #f1f5f9" }}
                />
              ))}
              {extra > 0 && (
                <div style={{
                  width: 38, height: 38, borderRadius: 6, border: "1px solid #f1f5f9",
                  background: "#f8fafc", display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#9ca3af",
                }}>
                  +{extra}
                </div>
              )}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <p style={{
              margin: 0, fontSize: 12, color: "#374151", fontWeight: 500,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {itemSummary}
              {moreItems && <span style={{ color: "#9ca3af" }}>{moreItems}</span>}
            </p>
            <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>
              {items.length} item{items.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        {/* Row 3 — total · delivery time · CTA */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          paddingTop: 8, borderTop: "1px solid #f8fafc", gap: 8, flexWrap: "wrap",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>
              {fmt(order?.total)}
            </span>
            {isActive && order?.deliveryMethod && (
              <div style={{
                display: "flex", alignItems: "center", gap: 4,
                fontSize: 11, fontWeight: 600, color: cfg.color,
              }}>
                <Clock size={11} color={cfg.dot} />
                {order.deliveryMethod === "SELF" ? "Kitchen delivery" : "OwnFood delivery"}
              </div>
            )}
          </div>

          <Link
            to={`/order-details/${order?._id}`}
            style={{
              display: "inline-flex", alignItems: "center", gap: 3,
              padding: "7px 14px", borderRadius: 7,
              background: isActive ? cfg.dot : "#f9fafb",
              color: isActive ? "#fff" : "#374151",
              border: isActive ? "none" : "1px solid #e5e7eb",
              fontSize: 12, fontWeight: 600, textDecoration: "none", flexShrink: 0,
            }}
          >
            View <ChevronRight size={13} />
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

/* ── Skeleton ────────────────────────────────── */
function OrderSkeleton() {
  return (
    <div style={{ background: "#fff", border: "1px solid #f1f5f9", borderLeft: "4px solid #e5e7eb", borderRadius: 12, padding: "12px 14px", marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <Skeleton height={16} width={150} />
        <Skeleton height={16} width={60} borderRadius={20} />
      </div>
      <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
        {[1,2,3].map(i => <Skeleton key={i} width={38} height={38} borderRadius={6} />)}
      </div>
      <Skeleton height={13} width="70%" style={{ marginBottom: 3 }} />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, paddingTop: 8, borderTop: "1px solid #f8fafc" }}>
        <Skeleton height={20} width={60} />
        <Skeleton height={30} width={80} borderRadius={7} />
      </div>
    </div>
  );
}

/* ── Page ────────────────────────────────────── */
export default function MyOrders() {
  const [orders, setOrders]     = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [isLoadingMore, setLoadingMore] = useState(false);
  const [nextPage, setNextPage] = useState(null);
  const [total, setTotal]       = useState(0);
  const [tab, setTab]           = useState("all");

  const activeTab = TABS.find((t) => t.key === tab) || TABS[0];

  // Paged rather than one big fetch — a long-standing customer's order list
  // would otherwise grow unbounded on every visit.
  const loadPage = useCallback(async (page, status) => {
    const first = page === 1;
    try {
      if (first) setLoading(true); else setLoadingMore(true);
      const res = await getMyOrders({ page, limit: 20, status: status || undefined });
      const rows = Array.isArray(res?.data) ? res.data : [];
      setOrders((prev) => (first ? rows : [...prev, ...rows]));
      setTotal(res?.pagination?.total ?? rows.length);
      setNextPage(res?.hasMore ? res.nextPage : null);
    } catch (e) {
      if (first) setOrders([]);
      toast.error(e?.message || "Failed to load orders");
    } finally {
      if (first) setLoading(false); else setLoadingMore(false);
    }
  }, []);

  // Switching tabs restarts at page 1 with the matching server-side filter.
  useEffect(() => { loadPage(1, activeTab.status); }, [loadPage, activeTab.status]);

  const filtered = useMemo(
    () =>
      activeTab.clientFiltered
        ? orders.filter((o) => ACTIVE_S.includes(o?.status?.toLowerCase()))
        : orders,
    [orders, activeTab],
  );

  return (
    <div className="home-content-shell page-navbar-space pb-5">

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <h5 style={{ fontWeight: 800, fontSize: 22, margin: 0, fontFamily: "'Inter', sans-serif", color: "#1a1a2e" }}>
          My Orders
        </h5>
        {!isLoading && (
          <span style={{ fontSize: 12, color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>
            {/* "Active" counts only what's loaded, so it's marked open-ended. */}
            {activeTab.clientFiltered
              ? `${filtered.length}${nextPage ? "+" : ""} shown`
              : `${total} total`}
          </span>
        )}
      </div>

      {/* Tabs — always available, so an empty tab is never a dead end */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, overflowX: "auto", paddingBottom: 2, scrollbarWidth: "none" }}>
        {TABS.map(t => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                flexShrink: 0, padding: "7px 16px", borderRadius: 20,
                border: active ? "none" : "1.5px solid #e5e7eb",
                background: active ? "#0d6efd" : "#fff",
                color: active ? "#fff" : "#374151",
                fontSize: 13, fontWeight: 600,
                fontFamily: "'Inter', sans-serif", cursor: "pointer",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Skeletons */}
      {isLoading && [1,2,3].map(i => <OrderSkeleton key={i} />)}

      {/* Empty — nothing at all, on the unfiltered tab */}
      {!isLoading && tab === "all" && orders.length === 0 && (
        <div style={{
          background: "#fff", border: "1.5px solid #f1f5f9", borderRadius: 14,
          padding: "60px 20px", textAlign: "center", fontFamily: "'Inter', sans-serif",
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: "50%", background: "#f1f5f9",
            margin: "0 auto 16px",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <ShoppingBag size={28} color="#d1d5db" />
          </div>
          <p style={{ fontSize: 15, fontWeight: 600, color: "#374151", margin: "0 0 6px" }}>No orders yet</p>
          <p style={{ fontSize: 13, color: "#9ca3af", margin: "0 0 20px" }}>
            Looks like you haven't placed any order.
          </p>
          <Link to="/all-food" style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "10px 22px", borderRadius: 8,
            background: "#0d6efd", color: "#fff",
            fontSize: 13, fontWeight: 600, textDecoration: "none",
          }}>
            Browse Foods
          </Link>
        </div>
      )}

      {/* Empty — this tab has nothing */}
      {!isLoading && tab !== "all" && filtered.length === 0 && (
        <div style={{
          textAlign: "center", padding: "48px 0",
          color: "#9ca3af", fontFamily: "'Inter', sans-serif", fontSize: 14,
        }}>
          No {activeTab.label.toLowerCase()} orders
          {activeTab.clientFiltered && nextPage ? " in what's loaded so far." : "."}
        </div>
      )}

      {/* List */}
      <AnimatePresence mode="wait">
        {!isLoading && filtered.map(order => (
          <OrderCard key={order._id} order={order} />
        ))}
      </AnimatePresence>

      {!isLoading && (
        <LoadMore
          hasMore={!!nextPage}
          isLoading={isLoadingMore}
          onClick={() => loadPage(nextPage, activeTab.status)}
          label="Load older orders"
        />
      )}
    </div>
  );
}
